import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { istTime } from "../../domain/istTime";
import {
  STATUS_LABEL,
  acceptNameRequest,
  consentText,
  declineNameRequest,
  getInvoiceName,
  invoiceNameRefusal,
  listNameRequests,
  nameSummary,
  requestUpstreamName,
  chooseOwnName,
  legalName,
  ownNameBlocker,
  withdrawNameConsent,
  type ConsentStatus,
  type InvoiceNameConsent,
} from "../../api/invoiceName";

const holdsAny = (roles: string[], allowed: readonly string[]) => roles.some((r) => allowed.includes(r.toLowerCase()));

const STATUS_COLOR: Record<ConsentStatus, "default" | "success" | "warning" | "error"> = {
  requested: "warning",
  accepted: "success",
  declined: "error",
  cancelled: "default",
  withdrawn: "default",
};

/**
 * Billing › Name on invoices (owner OB-12, 2026-10-28; radaccess v1.198 + SQL 277).
 *
 * Top: whose name is on THIS operator's internet invoices -- its own (needs
 * its GSTIN) or its upstream's (needs the upstream's consent) -- and the
 * buttons to ask or to go back to its own name.
 * Below: requests other operators made to use THIS operator's name, which
 * its Owner or Admin accepts, declines or later withdraws.
 * Owner and Admin act; Billing and superadmin read. Nothing is deleted.
 */
export function InvoiceNameScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const canWrite = holdsAny(identity.roles, ["operator", "manager"]);
  const qc = useQueryClient();
  const stateKey = ["invoice-name", ns];
  const requestsKey = ["invoice-name-requests", ns];
  const state = useQuery({
    queryKey: stateKey,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getInvoiceName(identity, ns, signal),
    retry: false,
  });
  const requests = useQuery({
    queryKey: requestsKey,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listNameRequests(identity, ns, signal),
    retry: false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: stateKey });
    void qc.invalidateQueries({ queryKey: requestsKey });
  };
  const ask = useMutation({ mutationFn: () => requestUpstreamName(identity, ns), onSuccess: refresh });
  const own = useMutation({ mutationFn: () => chooseOwnName(identity, ns), onSuccess: refresh });
  const [accepting, setAccepting] = useState<InvoiceNameConsent | null>(null);
  const [declining, setDeclining] = useState<InvoiceNameConsent | null>(null);
  const [withdrawing, setWithdrawing] = useState<InvoiceNameConsent | null>(null);

  const s = state.data;
  const toThem = s ? legalName(s.open?.upstreamLegalName ?? s.upstreamLegalName, "your upstream provider") : undefined;
  // Cancel takes Ask's place on screen, so it asks first: a double click on
  // Ask cancelled the owner's request 4 s later (2026-10-29).
  const [confirmCancel, setConfirmCancel] = useState(false);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Name on invoices
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Whose name, GSTIN and invoice series your internet invoices carry. Without a telecom licence you can use your own name (with your GSTIN, or none if you are not registered for GST) or, if it agrees, your upstream provider's.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1">Your internet invoices</Typography>
        {state.isLoading && <Typography color="text.secondary">Loading…</Typography>}
        {state.error && <Alert severity="error">{invoiceNameRefusal(state.error)}</Alert>}
        {s && (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography>{nameSummary(s)}</Typography>
            {ownNameBlocker(s) && <Alert severity="warning">{ownNameBlocker(s)}</Alert>}
            {s.identity === "branch_of_upstream" && s.open?.status === "accepted" && (
              <Typography variant="body2" color="text.secondary">
                {legalName(s.open.upstreamLegalName, "your upstream provider")} agreed{s.open.decidedAt ? ` on ${istTime(s.open.decidedAt)}` : ""}
                {s.open.consentTextVersion === "parent-created" ? " when it created your account." : "."}
              </Typography>
            )}
            {s.holdsLicence && (
              <Typography variant="body2" color="text.secondary">
                You hold a telecom licence, so your invoices are normally in your own name.
              </Typography>
            )}
            {!s.upstream && s.hasLicenceRecord && s.identity === "own" && (
              <Typography variant="body2" color="text.secondary">
                To use an upstream provider's name, first name it in Edit profile.
              </Typography>
            )}
            {canWrite && s.hasLicenceRecord && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {s.identity === "own" && !s.open && s.upstream && (
                  <Button variant="contained" disabled={ask.isPending} onClick={() => ask.mutate()}>
                    Ask {legalName(s.upstreamLegalName, "your upstream provider")} to invoice in its name
                  </Button>
                )}
                {(s.identity === "branch_of_upstream" || s.open) && (
                  <Button variant="outlined"
                    disabled={own.isPending || (s.identity === "branch_of_upstream" && s.mayIssueOwn === false)}
                    onClick={() => (s.open?.status === "requested" ? setConfirmCancel(true) : own.mutate())}>
                    {s.open?.status === "requested" ? "Cancel the request" : "Use my own name instead"}
                  </Button>
                )}
              </Stack>
            )}
            {s.open?.status === "requested" && toThem && (
              <Alert severity="info">
                Asked {toThem} on {istTime(s.open.requestedAt)}. Its Owner or Admin answers in Billing › Name on invoices.
              </Alert>
            )}
            {(ask.error || own.error) && <Alert severity="error">{invoiceNameRefusal(ask.error ?? own.error)}</Alert>}
            {s.history.length > 0 && <History rows={s.history} who="upstream" />}
          </Stack>
        )}
      </Paper>

      {(requests.data?.length ?? 0) > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1">Requests to use your name</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Operators below you asking for their internet invoices to be issued in your name, under your GSTIN and series.
          </Typography>
          <History
            rows={requests.data ?? []}
            who="operator"
            actions={(r) =>
              canWrite && (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {r.status === "requested" && (
                    <>
                      <Button size="small" variant="contained" onClick={() => setAccepting(r)}>
                        Agree
                      </Button>
                      <Button size="small" color="warning" onClick={() => setDeclining(r)}>
                        Decline
                      </Button>
                    </>
                  )}
                  {r.status === "accepted" && (
                    <Button size="small" color="warning" onClick={() => setWithdrawing(r)}>
                      Withdraw
                    </Button>
                  )}
                </Stack>
              )
            }
          />
        </Paper>
      )}
      {requests.error && <Alert severity="error">{invoiceNameRefusal(requests.error)}</Alert>}

      {confirmCancel && (
        <Dialog open onClose={() => setConfirmCancel(false)} fullWidth maxWidth="xs">
          <DialogTitle>Cancel your request?</DialogTitle>
          <DialogContent>
            <Typography>
              {toThem} will no longer see it, and your internet invoices stay in your own name. You can ask again later.
            </Typography>
            {own.error && <Alert severity="error" sx={{ mt: 2 }}>{invoiceNameRefusal(own.error)}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmCancel(false)}>Keep the request</Button>
            <Button color="warning" variant="contained" disabled={own.isPending}
              onClick={() => own.mutate(undefined, { onSuccess: () => setConfirmCancel(false) })}>
              Cancel the request
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {accepting && (
        <AcceptDialog
          request={accepting}
          run={() => acceptNameRequest(identity, ns, accepting.id)}
          onClose={() => setAccepting(null)}
          onDone={() => {
            refresh();
            setAccepting(null);
          }}
        />
      )}
      {declining && (
        <ReasonDialog
          title={`Decline ${legalName(declining.namespaceLegalName)}'s request`}
          help="Its invoices stay in its own name. It can ask again later."
          action="Decline"
          run={(note) => declineNameRequest(identity, ns, declining.id, note)}
          onClose={() => setDeclining(null)}
          onDone={() => {
            refresh();
            setDeclining(null);
          }}
        />
      )}
      {withdrawing && (
        <ReasonDialog
          title={`Withdraw consent from ${legalName(withdrawing.namespaceLegalName)}`}
          help="From its next invoice it uses its own name (and its GSTIN, if registered). Without a verified telecom licence or accepted Internet Distributor responsibility, its internet sales and invoices are then refused. Invoices already issued stay as they are."
          action="Withdraw"
          run={(note) => withdrawNameConsent(identity, ns, withdrawing.id, note)}
          onClose={() => setWithdrawing(null)}
          onDone={() => {
            refresh();
            setWithdrawing(null);
          }}
        />
      )}
    </Box>
  );
}

function History({
  rows,
  who,
  actions,
}: {
  rows: InvoiceNameConsent[];
  who: "upstream" | "operator";
  actions?: (r: InvoiceNameConsent) => React.ReactNode;
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{who === "upstream" ? "Asked" : "Operator"}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Asked on</TableCell>
            <TableCell>Answered</TableCell>
            {actions && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{who === "upstream" ? legalName(r.upstreamLegalName, "the upstream") : legalName(r.namespaceLegalName)}</TableCell>
              <TableCell>
                <Chip size="small" color={STATUS_COLOR[r.status]} label={STATUS_LABEL[r.status]} />
              </TableCell>
              <TableCell>{istTime(r.requestedAt)}</TableCell>
              <TableCell>
                {r.decidedAt ? istTime(r.decidedAt) : "—"}
                {r.decisionNote && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {r.decisionNote}
                  </Typography>
                )}
              </TableCell>
              {actions && <TableCell align="right">{actions(r)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AcceptDialog({
  request,
  run,
  onClose,
  onDone,
}: {
  request: InvoiceNameConsent;
  run: () => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}) {
  const go = useMutation({ mutationFn: run, onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Agree to invoice in your name</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography>{consentText(legalName(request.namespaceLegalName))}</Typography>
          <Typography variant="caption" color="text.secondary">
            Recorded with your login and the time, as consent wording {request.consentTextVersion}.
          </Typography>
          {go.error && <Alert severity="error">{invoiceNameRefusal(go.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={go.isPending} onClick={() => go.mutate()}>
          I agree
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReasonDialog({
  title,
  help,
  action,
  run,
  onClose,
  onDone,
}: {
  title: string;
  help: string;
  action: string;
  run: (reason: string) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const go = useMutation({ mutationFn: () => run(reason.trim()), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {help}
          </Typography>
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} required autoFocus />
          {go.error && <Alert severity="error">{invoiceNameRefusal(go.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={reason.trim() === "" || go.isPending} onClick={() => go.mutate()}>
          {action}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
